import dayjs from 'dayjs'
import L from 'leaflet'
import { useEffect, useRef, useState } from 'react'
import ReactDOMServer from 'react-dom/server'

import { OLMSApiService } from '@/shared/apis'
import { TruckMarkerIcon } from '@/shared/components/MapIcons/TruckMarkerIcon'
import { EntityId } from '@reduxjs/toolkit'

import { LiveLocation } from '../models/LiveLocation'

// Type for historical location storage
type LocationHistory = {
  [shiftId: string]: {
    locations: LiveLocation[]
    polyline: L.Polyline | null
  }
}

export type TrackLiveLocationsArgs = {
  map: L.Map | null
  selectedShiftIds: EntityId[]
}

/**
 * The following hook is used to draw the live locations and the live location trace on the map.
 * It polls the new live location every 15 seconds and store the value inside an object in memory.
 * In addition to drawing the current GPS location marker, it will also draw historical traces and
 *  connect them using polylines.
 * 
 * @param map the Leaflet map object
 * @param selectedShiftIds the selected shift ids to display the live location
 * @returns void
 */

export const useTrackLiveLocations = ({
  map,
  selectedShiftIds,
}: TrackLiveLocationsArgs) => {
  const [liveLocations, setLiveLocations] = useState<LiveLocation[]>([])
  const truckMarkersRef = useRef<L.LayerGroup | null>(null)
  const historyLayerRef = useRef<L.LayerGroup | null>(null)
  const locationHistoryRef = useRef<LocationHistory>({})
  const pollingIntervalRef = useRef<NodeJS.Timeout>()

  const createTruckIcon = (location: LiveLocation) => {
    const svgString = ReactDOMServer.renderToStaticMarkup(
      <TruckMarkerIcon fillColor={location.uiColor || '#000'} />,
    )

    return L.divIcon({
      html: svgString,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 34],
    })
  }

  const createTruckTooltip = (location: LiveLocation) => {
    return `
      <p style='margin: 2px 0'><strong>Vehicle:</strong> ${location.runName}</p>
      <p style='margin: 2px 0'><strong>Last Updated:</strong> ${new Date(location.deviceTime).toLocaleString()}</p>
      <p style='margin: 2px 0'><strong>Speed:</strong> ${location.speed} km/h</p>
      <p style='margin: 2px 0'><strong>Location:</strong> ${location.fullAddress}</p>
    `
  }

  // Initialize history layer
  useEffect(() => {
    if (!map) return

    historyLayerRef.current = L.layerGroup().addTo(map)

    return () => {
      if (historyLayerRef.current) {
        historyLayerRef.current.clearLayers()
        historyLayerRef.current = null
      }
    }
  }, [map])

  // Setup polling for live locations and update historical locations
  const updateLocationHistory = (location: LiveLocation) => {
    const shiftId = location.shiftId.toString()

    if (!locationHistoryRef.current[shiftId]) {
      locationHistoryRef.current[shiftId] = {
        locations: [],
        polyline: null,
      }
    }

    const existingIndex = locationHistoryRef.current[shiftId].locations.findIndex(
      loc => loc.id === location.id,
    )

    // Add new location to history if it doesn't exist
    if (existingIndex === -1) {
      locationHistoryRef.current[shiftId].locations.push(location)

      if (locationHistoryRef.current[shiftId].locations.length > 100) {
        locationHistoryRef.current[shiftId].locations.shift()
      }
    }
  }

  // Clear all history layers
  const clearHistoryLayers = () => {
    if (historyLayerRef.current) {
      historyLayerRef.current.clearLayers()
    }
  }

  const fetchLiveLocations = async () => {
    if (!selectedShiftIds.length) return

    try {
      const response = await OLMSApiService.post<LiveLocation[]>('/tracking/live', {
        date: dayjs().format('YYYY-MM-DD'),
        shiftIds: selectedShiftIds,
      })

      if (response) {
        setLiveLocations(response)

        // Clear existing layers
        clearHistoryLayers()

        // Update history for each location
        response.forEach(location => {
          if (location.x && location.y) {
            updateLocationHistory(location)
          }
        })

        // Redraw historical paths
        drawHistoricalPath()
      }
    } catch (error) {
      console.error('Error fetching live locations:', error)
    }
  }

  // Initialize live locations polling (initial poll & poll every 15 seconds)
  useEffect(() => {
    if (selectedShiftIds && selectedShiftIds.length > 0) {
      fetchLiveLocations()
      
      pollingIntervalRef.current = setInterval(fetchLiveLocations, 15000)
    } else {
      // Clear all layers shifts are selected
      if (historyLayerRef.current) {
        historyLayerRef.current.clearLayers()
      }

      if (truckMarkersRef.current) {
        truckMarkersRef.current.clearLayers()
      }

      locationHistoryRef.current = {}
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShiftIds])

  const drawHistoricalPath = () => {
    if (!map || !historyLayerRef.current) return

    // Clear existing history paths
    historyLayerRef.current.clearLayers()

    Object.entries(locationHistoryRef.current).forEach(([_, history]) => {
      if (history.locations.length < 2 || !historyLayerRef.current) return

      const color = history.locations[history.locations.length - 1].uiColor || '#000'

      // Create path coordinates and the lines between them
      const pathCoords = history.locations
        .filter(loc => loc.x && loc.y)
        .map(loc => [loc.y, loc.x] as [number, number])

      const polyline = L.polyline(pathCoords, {
        color: color,
        weight: 3,
        opacity: 0.5,
        smoothFactor: 1,
      })

      historyLayerRef.current.addLayer(polyline)

      // Add location dots with tooltips
      history.locations.forEach((location, index) => {
        if (!location.x || !location.y || !historyLayerRef.current) return

        const dotIcon = L.divIcon({
          html: `<div style="
            width: 8px;
            height: 8px;
            background-color: ${color};
            border-radius: 50%;
            opacity: 0.7;">
          </div>`,
          className: '',
          iconSize: [8, 8],
        })

        const marker = L.marker([location.y, location.x], {
          icon: dotIcon,
          interactive: true,
        })

        const tooltip = `
          <p style='margin: 2px 0'><strong>Trace ID:</strong> ${index + 1}</p>
          <p style='margin: 2px 0'><strong>Time:</strong> ${new Date(location.deviceTime).toLocaleString()}</p>
          <p style='margin: 2px 0'><strong>Location:</strong> ${location.fullAddress}</p>
        `

        marker.bindTooltip(tooltip, {
          permanent: false,
          direction: 'top',
        })

        historyLayerRef.current.addLayer(marker)
      })

      // Store reference to current polyline
      history.polyline = polyline
    })
  }


  // Update truck markers on the map
  useEffect(() => {
    if (!map) return

    if (truckMarkersRef.current) {
      truckMarkersRef.current.clearLayers()
    } else {
      truckMarkersRef.current = L.layerGroup().addTo(map)
    }

    liveLocations.forEach(location => {
      if (!location.x || !location.y) return

      const marker = L.marker([location.y, location.x], {
        icon: createTruckIcon(location),
        zIndexOffset: 100,
      })

      marker.bindTooltip(
        createTruckTooltip(location),
        {
          permanent: false,
          direction: 'right',
          offset: L.point(15, -25),
        },
      )

      truckMarkersRef.current!.addLayer(marker)
    })

    return () => {
      if (truckMarkersRef.current) {
        truckMarkersRef.current.clearLayers()
        truckMarkersRef.current = null
      }
    }
  }, [map, liveLocations])



  return {
    liveLocations,
    locationHistory: locationHistoryRef.current,
  }
}